import { Link } from 'react-router-dom';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const steps = [
  {
    number: '01',
    title: 'Purchase a License Key',
    content: 'Visit the PowerLottery.co home page and complete the PayPal checkout. Your $79 license key is good for 52 reports — one full year of weekly draws for both POWERBALL and MEGAMILLIONS. You will receive your license key by email within 24 hours of purchase.'
  },
  {
    number: '02',
    title: 'Enter Your License Key',
    content: 'Access the Analytics and Reporting engine, Enter the license key you received via email. . The system will validate it instantly and unlock your report access. Keep your key private — it is tied to a single user account.'
  },
  {
    number: '03',
    title: 'Select a Lottery',
    content: 'Use the lottery selector to choose either POWERBALL or MEGAMILLIONS. Each lottery maintains its own independent dataset and analytical models. You can switch between lotteries at any time for a report credit.'
  },
  {
    number: '04',
    title: 'Generate Your Report',
    content: 'Click the "Export Report PDF" button to run the latest analysis. Each report is generated on-demand using drawing data through the most recent official draw. Reports are date-stamped so you always know which draws are included.'
  },
  {
    number: '05',
    title: 'Read the Hot & Cold Numbers',
    content: 'The Hot Numbers section shows balls that have appeared far more frequently than statistical expectation over the tast 90 and 30 day draw windows. Cold Numbers shows the opposite — balls that are significantly underrepresented. Neither is a guarantee, but both highlight meaningful deviations from random expectation.'
  },
  {
    number: '06',
    title: 'Identify Trending Numbers',
    content: 'The Pattern Analysis & Insights shows numbers whose frequency is accelerating — they are appearing more often in recent draws than in older draws within the 30 and 90 day windows. Trending Down numbers are fading. These momentum signals can be useful for short-term number selection strategies.'
  },
  {
    number: '07',
    title: 'Pick Your Numbers',
    content: 'Weekly Analytics Generated Tickets combine analysis and insights from the report to build generated, statistical number selections. Many players use a mix of hot numbers for frequency confidence and trending-up numbers for momentum combinations.'
  }
];

const faqs = [
  {
    q: 'How often is the drawing data updated?',
    a: 'Drawing results are pulled from official lottery sources within hours of each draw. POWERBALL draws on Monday, Wednesday, and Saturday. MEGAMILLIONS draws on Tuesday and Friday.'
  },
  {
    q: 'Does the license key expire?',
    a: 'Licenses keys do not expire. Your 52 reports can be used at any pace — all in one week or spread across the year.'
  },
  {
    q: 'Can I use the same license key on multiple devices?',
    a: 'Yes. Your license key can be entered on any browser or device. There is no device limit, but keys are non-transferable and tied to the purchasing account. Anytime a report is run, your license key reamining credits are reduced.'
  },
  {
    q: 'Does PowerLottery guarantee a win?',
    a: 'No. The lottery is a game of chance and no system can guarantee a winning ticket. PowerLottery.co provides statistical analysis, identifies trends, bias, and anomalies to help you make more informed selections — nothing more.'
  }
];

const userGuideSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': `${siteUrl}/user-guide`,
      url: `${siteUrl}/user-guide`,
      name: 'User Guide – PowerLottery.co',
      description:
        'Step-by-step instructions for using PowerLottery.co lottery analytics. Learn how to purchase a license key, generate reports, and interpret hot numbers, cold numbers, and trends.',
      isPartOf: { '@id': `${siteUrl}/#website` },
    },
    {
      '@type': 'HowTo',
      name: 'How to Use PowerLottery.co Lottery Analytics',
      description:
        'Follow these steps to get the most out of your PowerLottery.co subscription for POWERBALL and MegaMillions analytics.',
      step: steps.map((s) => ({
        '@type': 'HowToStep',
        name: s.title,
        text: s.content,
        position: parseInt(s.number, 10),
      })),
    },
    {
      '@type': 'FAQPage',
      mainEntity: faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.a,
        },
      })),
    },
  ],
};

export default function UserGuidePage() {
  return (
    <>
      <title>User Guide – PowerLottery.co Lottery Analytics Step-by-Step</title>
      <meta name="description" content="Learn how to use PowerLottery.co: purchase a license key, generate POWERBALL and MegaMillions analytics reports, and interpret hot numbers, cold numbers, and trend data." />
      <meta name="keywords" content="lottery analytics guide, powerball user guide, megamillions report instructions, lottery number analysis" />
      <link rel="canonical" href={`${siteUrl}/user-guide`} />
      <meta property="og:url" content={`${siteUrl}/user-guide`} />
      <meta property="og:title" content="User Guide – PowerLottery.co Lottery Analytics Step-by-Step" />
      <meta property="og:description" content="Step-by-step instructions for using PowerLottery.co: generate weekly POWERBALL and MegaMillions analytics reports and interpret the results." />
      <meta name="twitter:title" content="User Guide – PowerLottery.co Lottery Analytics Step-by-Step" />
      <meta name="twitter:description" content="Step-by-step instructions for using PowerLottery.co: generate weekly POWERBALL and MegaMillions analytics reports and interpret the results." />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(userGuideSchema) }}
      />
      <div className="min-h-screen bg-white font-['Inter',sans-serif]">
        {/* Header */}
        <header className="bg-black py-12 text-center border-b border-gray-100">
          <div className="flex justify-center items-center mb-6">
            <img
              src="https://public.readdy.ai/ai/img_res/a5d5b9d0-9163-482d-be03-b47dc644bd65.png"
              alt="PowerLottery.co Logo"
              className="w-16 h-16 mr-4 object-contain"
            />
            <h1 className="text-5xl font-bold">
              <Link to="/" className="text-white bg-black px-2 py-1 rounded-lg mr-1">Power</Link>
              <span className="text-[#FF69B4]">Lottery.co</span>
            </h1>
          </div>
          <p className="text-2xl text-white font-medium">Smarter Lottery Players</p>
        </header>

        {/* Nav breadcrumb */}
        <div className="bg-gray-50 border-b border-gray-200">
          <div className="max-w-5xl mx-auto px-6 py-3 flex items-center space-x-2 text-sm text-gray-500">
            <Link to="/" className="hover:text-[#FF69B4] transition-colors cursor-pointer">Home</Link>
            <span>/</span>
            <span className="text-gray-800 font-medium">User Guide</span>
          </div>
        </div>

        <main className="max-w-5xl mx-auto px-6 py-16">
          {/* Page title */}
          <div className="mb-14 text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">User Guide</h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">
              Follow the steps below to get the most out of your PowerLottery.co subscription.
            </p>
          </div>

          {/* Steps */}
          <div className="space-y-8 mb-20">
            {steps.map((step) => (
              <div key={step.number} className="bg-gray-50 rounded-xl p-8 flex gap-8 items-start">
                <div className="flex-shrink-0 w-14 h-14 flex items-center justify-center bg-black rounded-xl">
                  <span className="text-[#FF69B4] font-bold text-lg">{step.number}</span>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{step.title}</h3>
                  <p className="text-gray-600 text-base leading-relaxed">{step.content}</p>
                </div>
              </div>
            ))}
          </div>

          {/* FAQ */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center">Frequently Asked Questions</h2>
            <div className="space-y-6">
              {faqs.map((faq, i) => (
                <div key={i} className="bg-gray-50 rounded-xl p-8">
                  <p className="font-semibold text-gray-900 mb-3 text-base">{faq.q}</p>
                  <p className="text-gray-600 text-base leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="bg-black rounded-xl p-10 text-center">
            <p className="text-white text-xl font-medium mb-6">Ready to start playing smarter?</p>
            <Link
              to="/"
              className="inline-block bg-[#FF69B4] text-white font-semibold px-10 py-3 rounded-lg hover:bg-[#FF1493] transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Your License Key
            </Link>
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-black py-12 text-center border-t border-gray-700">
          <div className="max-w-4xl mx-auto px-6">
            <p className="text-white text-lg mb-4">
              The lottery is a game of chance. Winning is not guaranteed. Play responsibly.
            </p>
            <div className="flex justify-center items-center space-x-2 text-white">
              <span>©</span>
              <span className="font-medium">PowerLottery.co – All Rights Reserved.</span>
            </div>
            <div className="mt-4">
              <a
                href="https://readdy.ai/?ref=logo"
                className="text-gray-300 text-sm hover:text-white transition-colors cursor-pointer"
              >
                Website Builder
              </a>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
